import React, {useState, useMemo, memo} from "react";
import {useTranslation} from "react-i18next";
import {inject, observer} from "mobx-react";
import {Form, Formik} from "formik";
import {isEqual} from "lodash";
import * as yup from "yup";
//local
import {LocalModal} from "components/LocalModal";
import MessagePanel from "components/MessagePanel";
import EditDatePicker from "components/EditDatePicker";
import {IconNotificationCircle} from "components/icons";
import {Info} from "components/Info";
import {ButtonTransparent} from "components/buttons/ButtonTransparent";
import FormikField from "components/FormikField";
import RequestDocuments from "components/requestCard/RequestDocuments";
import {bulkProcurementOperations} from "constants/buyer.consts";
import {saveDocument} from "helpers/helper";
import {showError, showInfo} from "helpers/notifications.helper";
import {fileExtension, fileType} from "constants/file.consts";
import {dateFormat} from "helpers/datetime";
import {color} from "constants/color.consts";

const MessageToAll = memo(({user, loading, t, onSend}) => (
  <React.Fragment>
    <MessagePanel
      mode="create"
      loading={loading}
      message={{User: user}}
      placeholder={t("BULK_OPERATION_MODAL_PROCUREMENT_MESSAGE_PLACEHOLDER")}
      addClass="mt-4 mb-8 fz-14 hght-115"
      sendMessage={onSend}
      permissionsMessage={["CREATE_RFT_MESSAGES_MESSAGE"]}
      permissionsFile={["CREATE_RFT_MESSAGES_FILE"]}
      isMyMessage
    />

    <div className="df-row-center __normal fz-12 mla" style={{width: "fit-content"}}>
      <span className="mr-1">{t("BULK_OPERATION_MODAL_PROCUREMENT_DESCRIPTION")}</span>
      <Info tooltip="BULK_OPERATION_MODAL_PROCUREMENT_MESSAGE_DESCRIPTION_TOOLTIP" />
    </div>
  </React.Fragment>
));

const UpdateClosedAt = memo(({onSubmit, onClose}) => {
  const {t} = useTranslation();

  const setOfferDateSchema = yup.object().shape({
    closedAt: yup.date().min(new Date()),
  });

  return (
    <React.Fragment>
      <Formik
        initialValues={{closedAt: new Date()}}
        validationSchema={setOfferDateSchema}
        onSubmit={onSubmit}
      >
        {() => (
          <Form>
            <EditDatePicker
              name="closedAt"
              small
              allowClear={false}
              disabledDate={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
              addClass="mt-4 mb-8 wdth-150"
            />
            <div className="df-row-jsb-center">
              <div className="df-row-center __normal fz-12">
                <span className="mr-1">
                  {t("BULK_OPERATION_MODAL_PROCUREMENT_DESCRIPTION")}
                </span>
                <Info tooltip="BULK_OPERATION_MODAL_PROCUREMENT_CLOSEDAT_DESCRIPTION_TOOLTIP" />
              </div>
              <div className="df">
                <ButtonTransparent
                  onClick={onClose}
                  color="red"
                  name="CANCEL"
                  type="button"
                  medium
                />
                <ButtonTransparent
                  type="submit"
                  medium
                  addClass="ml-4"
                  name="BULK_OPERATION_MODAL_SAVE_BUTTON"
                />
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </React.Fragment>
  );
});

const CreateRevision = memo(({onSubmit, onClose}) => {
  const {t} = useTranslation();

  const [bulkRevision, setBulkRevision] = useState(null);

  const hasFiles = useMemo(() => {
    if (!bulkRevision) return false;
    const {contracts, documents, plans, links} = bulkRevision;
    return contracts.length + documents.length + plans.length + links.length > 0;
  }, [bulkRevision]);

  const createRevisionSchema = yup.object().shape({
    name: yup.string().required(),
  });

  return (
    <React.Fragment>
      <Formik
        initialValues={{name: null}}
        validationSchema={createRevisionSchema}
        onSubmit={(values) => onSubmit({...bulkRevision, name: values.name})}
      >
        {({values, dirty, isValid}) => (
          <Form>
            <div className="df-row-center mt-4">
              <FormikField
                name="name"
                placeholder={t("REVISION_MODAL_INPUT_PLACEHOLDER")}
                small
                highlightIfEmpty={!values.name}
                autoResize={{minWidth: "230px", maxWidth: "90%"}}
                height="40px"
                className="input"
              />
            </div>
            <RequestDocuments
              revisions={bulkRevision ? [bulkRevision] : []}
              allRevisionsView
              intoContract
              editable
              showTabs={false}
              confirmationOnRemove={false}
              columnTitles={{
                contracts: "CONTRACTDRAFT",
                documents: "DOCUMENTS",
                plans: "PLANS",
                links: "LINKS",
              }}
              customUpdateDocuments={(revision) => {
                const hasChanges = !isEqual(revision, bulkRevision);
                hasChanges && setBulkRevision(revision);
              }}
              addWrapperClass="mt-0"
            />
            <div className="df-row-jsb-center">
              <div className="df-row-center __normal fz-12">
                <span className="mr-1">
                  {t("BULK_OPERATION_MODAL_PROCUREMENT_DESCRIPTION")}
                </span>
                <Info tooltip="BULK_OPERATION_MODAL_PROCUREMENT_REVISION_DESCRIPTION_TOOLTIP" />
              </div>
              <div className="df">
                <ButtonTransparent
                  onClick={onClose}
                  color="red"
                  name="CANCEL"
                  type="button"
                  medium
                />
                <ButtonTransparent
                  type="submit"
                  medium
                  addClass="ml-4"
                  disabled={!dirty || !isValid || !hasFiles}
                  name="BULK_OPERATION_MODAL_CREATE_BUTTON"
                />
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </React.Fragment>
  );
});

const ExportToNext = memo(({loading, onExport}) => (
  <React.Fragment>
    <ButtonTransparent
      name="EXPORT"
      addClass="mt-4"
      small
      fullWidth
      loading={loading}
      onClick={onExport}
    />
  </React.Fragment>
));

const modalWidth = {
  [bulkProcurementOperations.PROCUREMENT_BULK_OPERATION_MESSAGE_TO_ALL]: 500,
  [bulkProcurementOperations.PROCUREMENT_BULK_OPERATION_UPDATE_CLOSED_AT]: 500,
  [bulkProcurementOperations.PROCUREMENT_BULK_OPERATION_CREATE_REVISION]: 1200,
  [bulkProcurementOperations.PROCUREMENT_BULK_OPERATION_EXPORT_TO_NEXT]: 350,
};

const needNotificationStep = {
  [bulkProcurementOperations.PROCUREMENT_BULK_OPERATION_MESSAGE_TO_ALL]: true,
  [bulkProcurementOperations.PROCUREMENT_BULK_OPERATION_UPDATE_CLOSED_AT]: true,
  [bulkProcurementOperations.PROCUREMENT_BULK_OPERATION_CREATE_REVISION]: true,
  [bulkProcurementOperations.PROCUREMENT_BULK_OPERATION_EXPORT_TO_NEXT]: false,
};

// export to Next is prepared on the server: its status is polled until the file is ready (~10 min max)
const POLL_INTERVAL = 3000;
const POLL_MAX_ATTEMPTS = 100;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const BulkRfpOperationModal = inject("store")(
  observer(
    ({store: {buyer, clientStore}, action, counters, currentUser, onSubmit, onClose}) => {
      const {t} = useTranslation();
      const {currentProject} = buyer;

      const [innerLoading, setInnerLoading] = useState(false);
      const [confirmationStep, setConfirmationStep] = useState(null);
      const [isNotificationStep, setNotificationStep] = useState(false);
      const [values, setValues] = useState(null);

      const isAdditionalSteps = useMemo(
        () => confirmationStep || isNotificationStep,
        [confirmationStep, isNotificationStep]
      );
      const additionalStepsTitle = useMemo(
        () =>
          confirmationStep
            ? t("BULK_OPERATION_MODAL_CONFIRMATION_TITLE")
            : isNotificationStep
            ? t("BULK_OPERATION_MODAL_NOTIFICATION_TITLE")
            : null,
        [confirmationStep, isNotificationStep, t]
      );

      const waitingNextFile = ({processId}) => {
        const projectName = currentProject.name ?? "project";

        const poll = (attempt = 1) =>
          clientStore
            .getUserProcess(processId)
            .then(async (process) => {
              if (["WAITING", "PROCESSING"].includes(process.status)) {
                if (attempt >= POLL_MAX_ATTEMPTS)
                  return showError(t("BULK_OPERATION_MODAL_EXPORT_TO_NEXT_TIMEOUT"));
                return wait(POLL_INTERVAL).then(() => poll(attempt + 1));
              }
              if (process.status === "ERROR") return showError(process.errorMessage);
              if (process.status === "CANCELED")
                return showError(t("BULK_OPERATION_MODAL_EXPORT_TO_NEXT_CANCELED"));

              const response = await fetch(process.result.url);
              if (!response.ok)
                throw new Error(`File download failed: ${response.status}`);
              const fileData = await response.blob();
              const fileName = t("DOWNLOAD_PROCUREMENT_FILENAME").format(
                projectName,
                dateFormat(Date.now())
              );
              saveDocument(fileData, fileType[fileExtension.XLSX], fileName);
            })
            .catch((error) => {
              const status = error.response?.status;
              const timeoutError = t("BULK_OPERATION_MODAL_EXPORT_TO_NEXT_TIMEOUT");
              if (status >= 400 && status < 500)
                return showError(error.response.data?.error ?? timeoutError);
              if (attempt < POLL_MAX_ATTEMPTS)
                return wait(POLL_INTERVAL).then(() => poll(attempt + 1));
              showError(timeoutError);
            });

        // the export lives only in this tab: the browser asks before leaving the page.
        // A handler per export, so the first finished one does not unlock a running one
        const preventLeaving = (event) => {
          event.preventDefault();
          event.returnValue = "";
        };

        showInfo(t("BULK_OPERATION_MODAL_EXPORT_TO_NEXT_STARTED"), 10);
        window.addEventListener("beforeunload", preventLeaving);
        poll().finally(() => window.removeEventListener("beforeunload", preventLeaving));
      };

      const uploadMessageToAllFiles = async () => {
        const rfpId = window.crypto.randomUUID();
        const data = values[confirmationStep];
        const filesData = data.files;
        if (!filesData?.length) return;
        setInnerLoading(true);
        const dataRequests = filesData.map((file) =>
          buyer.messageUploadFile({rfpId, file})
        );
        const files = (await Promise.allSettled(dataRequests))
          .filter((item) => item.status === "fulfilled")
          .map((item) => item.value);

        setInnerLoading(false);
        return {
          ...values,
          [confirmationStep]: {...values[confirmationStep], files},
        };
      };

      const actionDataProcessing = {
        [bulkProcurementOperations.PROCUREMENT_BULK_OPERATION_MESSAGE_TO_ALL]: undefined,
        [bulkProcurementOperations.PROCUREMENT_BULK_OPERATION_UPDATE_CLOSED_AT]:
          undefined,
        [bulkProcurementOperations.PROCUREMENT_BULK_OPERATION_CREATE_REVISION]: undefined,
        [bulkProcurementOperations.PROCUREMENT_BULK_OPERATION_EXPORT_TO_NEXT]:
          waitingNextFile,
      };

      const prepareDataProcessing = {
        [bulkProcurementOperations.PROCUREMENT_BULK_OPERATION_MESSAGE_TO_ALL]:
          uploadMessageToAllFiles,
        [bulkProcurementOperations.PROCUREMENT_BULK_OPERATION_UPDATE_CLOSED_AT]:
          undefined,
        [bulkProcurementOperations.PROCUREMENT_BULK_OPERATION_CREATE_REVISION]: undefined,
        [bulkProcurementOperations.PROCUREMENT_BULK_OPERATION_EXPORT_TO_NEXT]: undefined,
      };

      const ConfirmationPart = () => (
        <div className="df-row-jce-center">
          <ButtonTransparent
            onClick={onClose}
            medium
            color="red"
            name="CANCEL"
            type="button"
            disabled={innerLoading}
          />
          <ButtonTransparent
            name="BULK_OPERATION_MODAL_PROCEED_BUTTON"
            onClick={() => handleStartBulkOperationUpdate()}
            nonTransparent
            medium
            loading={innerLoading}
            addClass="ml-4"
          />
        </div>
      );

      const NotificationPart = () => (
        <ButtonTransparent
          name="BULK_OPERATION_MODAL_DONE_BUTTON"
          onClick={onClose}
          nonTransparent
          medium
          uppercase
          addClass="mla"
        />
      );

      const handleSendMessageToAll = async (message, filesData) => {
        const values = {
          actions: [bulkProcurementOperations.PROCUREMENT_BULK_OPERATION_MESSAGE_TO_ALL],
          [bulkProcurementOperations.PROCUREMENT_BULK_OPERATION_MESSAGE_TO_ALL]: {
            message,
            files: filesData,
          },
        };
        setValues(values);
        setConfirmationStep(
          bulkProcurementOperations.PROCUREMENT_BULK_OPERATION_MESSAGE_TO_ALL
        );
      };

      const handleSetOfferDate = ({closedAt}) => {
        const values = {
          actions: [
            bulkProcurementOperations.PROCUREMENT_BULK_OPERATION_UPDATE_CLOSED_AT,
          ],
          [bulkProcurementOperations.PROCUREMENT_BULK_OPERATION_UPDATE_CLOSED_AT]: {
            closedAt,
          },
        };
        setValues(values);
        setConfirmationStep(
          bulkProcurementOperations.PROCUREMENT_BULK_OPERATION_UPDATE_CLOSED_AT
        );
      };

      const handleCreateRevision = (values) => {
        const payload = {
          actions: [bulkProcurementOperations.PROCUREMENT_BULK_OPERATION_CREATE_REVISION],
          [bulkProcurementOperations.PROCUREMENT_BULK_OPERATION_CREATE_REVISION]: {
            ...values,
          },
        };
        setValues(payload);
        setConfirmationStep(
          bulkProcurementOperations.PROCUREMENT_BULK_OPERATION_CREATE_REVISION
        );
      };

      const handleExportToNext = () => {
        const values = {
          actions: [bulkProcurementOperations.PROCUREMENT_BULK_OPERATION_EXPORT_TO_NEXT],
          [bulkProcurementOperations.PROCUREMENT_BULK_OPERATION_EXPORT_TO_NEXT]: {
            needExport: true,
          },
        };
        setValues(values);
        handleStartBulkOperationUpdate(values);
      };

      const getNotificationStep = (actions) =>
        actions.some((action) => needNotificationStep[action])
          ? setNotificationStep(true)
          : onClose();
      const handleStartBulkOperationUpdate = async (data) => {
        const preparedData = await prepareDataProcessing[action]?.();
        const payload = data ?? preparedData ?? values;

        setInnerLoading(true);
        onSubmit(payload)
          .then((data) => {
            payload.actions.forEach((action) => actionDataProcessing[action]?.(data));
            getNotificationStep(payload.actions);
          })
          .finally(() => {
            setInnerLoading(false);
            setConfirmationStep(null);
          });
      };

      return (
        <LocalModal
          title={additionalStepsTitle}
          titleIcon={
            isAdditionalSteps ? (
              <IconNotificationCircle color={color.orange} addClass="mr-2" />
            ) : null
          }
          onClose={onClose}
          className={`mt-7 zi-2 __nonecase pa-${isAdditionalSteps ? 8 : 4} wdth-${
            isAdditionalSteps ? 500 : modalWidth[action]
          }`}
          style={{alignSelf: "flex-start"}}
        >
          {isAdditionalSteps ? (
            <React.Fragment>
              <div className="__normal fz-16 mt-4 mb-8">
                {t(
                  isNotificationStep
                    ? "BULK_OPERATION_MODAL_NOTIFICATION"
                    : `BULK_OPERATION_MODAL_CONFIRMATION_${t(confirmationStep)}`
                )}
              </div>
              {confirmationStep && <ConfirmationPart />}
              {isNotificationStep && <NotificationPart />}
            </React.Fragment>
          ) : (
            <React.Fragment>
              <div className="df-row-jsb-center">
                <span className="__bold fz-12 __uppercase">
                  {t(`BULK_OPERATION_MODAL_TITLE_${action}`)}
                </span>
                <span className="__normal fz-12">
                  {t("BULK_OPERATION_MODAL_TITLE_PROCUREMENT_COUNTS")?.format(
                    counters[0],
                    counters[1]
                  )}
                </span>
              </div>

              {bulkProcurementOperations.PROCUREMENT_BULK_OPERATION_MESSAGE_TO_ALL ===
                action && (
                <MessageToAll
                  user={currentUser}
                  loading={innerLoading}
                  t={t}
                  onSend={handleSendMessageToAll}
                />
              )}
              {bulkProcurementOperations.PROCUREMENT_BULK_OPERATION_UPDATE_CLOSED_AT ===
                action && (
                <UpdateClosedAt onSubmit={handleSetOfferDate} onClose={onClose} />
              )}
              {bulkProcurementOperations.PROCUREMENT_BULK_OPERATION_CREATE_REVISION ===
                action && (
                <CreateRevision onSubmit={handleCreateRevision} onClose={onClose} />
              )}
              {bulkProcurementOperations.PROCUREMENT_BULK_OPERATION_EXPORT_TO_NEXT ===
                action && (
                <ExportToNext loading={innerLoading} onExport={handleExportToNext} />
              )}
            </React.Fragment>
          )}
        </LocalModal>
      );
    }
  )
);
